import { FC } from 'react'
import { noop } from 'lodash'
import usePress from 'hooks/usePress'
import useHover from 'hooks/useHover'
import { Repeat } from 'lucide-react'
import useActionGuard from 'hooks/useActionGuard'
import { useIsMobile } from '@wellesley/ui'
import { ActivityTabs, getPostActivityRoute } from 'helpers/routes'
import { useGetApiV1PostsByPostIdRepostsQuery } from '@wellesley/openapi'

import { TRepost } from '../types'
import { CardAction } from '../../CardAction'
import PopupContent, {
  MAX_AVATARS,
  POPUP_OPEN_DELAY,
  POPUP_CLOSE_DELAY,
} from '../../Popup'

type TRepostButton = Pick<
  TRepost,
  'counter' | 'isHighlighted' | 'feed' | 'customContent'
> & {
  isDisabled: boolean
}

const RepostButton: FC<TRepostButton> = ({
  feed,
  counter,
  isDisabled,
  isHighlighted,
  customContent,
}) => {
  const { ActionTooltipWrapper } = useActionGuard()

  const isMobile = useIsMobile()

  const [isPressed, touchEvents] = usePress(POPUP_OPEN_DELAY, POPUP_CLOSE_DELAY)

  const [, isHovered, , handleMouseLeave, handleMouseEnter] =
    useHover<HTMLDivElement>({
      delay: POPUP_OPEN_DELAY,
      leaveDelay: POPUP_CLOSE_DELAY,
      useRefElement: false,
    })

  const canShowPopup = (isHovered || isPressed) && counter > 0

  const { data: reposts, isLoading: isLoadingReposts } =
    useGetApiV1PostsByPostIdRepostsQuery(
      {
        postId: feed.id,
        kind: 'ALL',
        limit: MAX_AVATARS,
        skipDuplicates: true,
      },
      { skip: !canShowPopup },
    )

  const reposters = reposts?.map(({ user }) => user) || []

  return (
    <ActionTooltipWrapper>
      <div
        className="relative flex flex-row"
        {...(isMobile
          ? touchEvents
          : {
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
            })}
      >
        <CardAction
          value={counter}
          name="repost"
          handler={noop}
          hideValue={Boolean(customContent)}
        >
          {customContent || (
            <Repeat
              size={20}
              color={
                isDisabled
                  ? 'var(--gray-300)'
                  : isHighlighted
                    ? 'var(--blue-500)'
                    : 'var(--gray-600)'
              }
            />
          )}
          {canShowPopup && (
            <PopupContent
              users={reposters}
              counter={counter}
              isLoading={isLoadingReposts}
              actionButtonLink={getPostActivityRoute(
                feed.author,
                feed.id,
                ActivityTabs.reposts,
              )}
            />
          )}
        </CardAction>
      </div>
    </ActionTooltipWrapper>
  )
}

export default RepostButton
